import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { films, featuredFilmIds } from "@/mocks/films";

export default function HeroCarousel() {
  const slides = useMemo(
    () => featuredFilmIds.map((id) => films.find((film) => film.id === id)).filter((film) => film !== undefined),
    [],
  );
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (next: number) => {
      if (slides.length === 0) return;
      setActive((next + slides.length) % slides.length);
    },
    [slides.length],
  );

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  if (slides.length === 0) {
    return (
      <div className="flex h-[320px] items-center justify-center rounded-lg border border-background-200 bg-background-100 text-sm text-foreground-500">
        暂无焦点推荐，敬请期待
      </div>
    );
  }

  const current = slides[active];

  return (
    <section
      aria-label="焦点推荐"
      className="relative overflow-hidden rounded-lg bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[430px] w-full md:h-[520px]">
        {slides.map((film, index) => (
          <div
            key={film.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={film.backdrop}
              alt={`${film.title} 剧照`}
              title={`${film.title} 韩国影视作品剧照`}
              className="h-full w-full object-cover object-center"
            />
          </div>
        ))}
        <div className="carousel-mask absolute inset-0" />

        <div className="relative z-10 flex h-full w-full flex-col justify-end p-5 md:justify-center md:p-10 lg:p-12">
          <div className="w-full max-w-[560px]">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded bg-accent-500 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-950">
                焦点推荐
              </span>
              <span className="rounded border border-background-50/25 bg-primary-950/30 px-2 py-1 text-[11px] text-background-100 backdrop-blur-sm">
                {current.type} · {current.year}
              </span>
              <span className="flex items-center gap-1 rounded bg-primary-950/30 px-2 py-1 text-[11px] text-background-100 backdrop-blur-sm">
                <i className="ri-star-fill text-[10px] text-accent-400" />
                {current.rating.toFixed(1)}
              </span>
            </div>
            <h2 className="font-heading text-[30px] leading-tight font-semibold text-background-50 md:text-[42px]">
              {current.title}
            </h2>
            <p className="mt-1.5 text-[12px] font-light tracking-wide text-background-200">{current.originalTitle}</p>
            <p className="mt-4 max-w-lg text-[13px] leading-7 text-background-100 md:text-[14px]">{current.tagline}</p>
            <p className="mt-2 hidden max-w-lg text-[13px] leading-7 text-background-200/90 sm:block">
              {current.synopsis.slice(0, 78)}…
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-[13px] font-medium text-primary-950 transition-colors hover:bg-accent-400"
              >
                查看剧情详情
                <i className="ri-arrow-right-line text-sm" />
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/30 bg-primary-950/20 px-4 py-2.5 text-[13px] text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-950/40"
              >
                浏览全部分类
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 right-5 z-10 hidden items-center gap-2 md:flex">
          <button
            type="button"
            aria-label="上一个推荐"
            onClick={() => go(active - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-50/25 bg-primary-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-950/70"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            type="button"
            aria-label="下一个推荐"
            onClick={() => go(active + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-50/25 bg-primary-950/40 text-background-50 backdrop-blur-sm transition-colors hover:bg-primary-950/70"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>

        <div className="absolute bottom-5 left-5 z-10 flex items-center gap-1.5 md:left-10 lg:left-12">
          {slides.map((film, index) => (
            <button
              key={film.id}
              type="button"
              aria-label={`切换到 ${film.title}`}
              onClick={() => go(index)}
              className={`h-1 rounded-full transition-all duration-300 ${
                index === active ? "w-7 bg-accent-500" : "w-3 bg-background-50/45 hover:bg-background-50/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}